// Resolve runtime config from window.__ENV__ (templated by the nginx
// entrypoint) with fallback to build-time VITE_* values. Anything reading env
// must go through here so the dev/prod story is identical.
//
// Defaults are SAME-ORIGIN, never hardcoded localhost: in prod the SPA is
// served by the same nginx that reverse-proxies /auth, /api, /tenants,
// /graphql and /ws to the backend services, so an empty base + the per-client
// path prefix resolves to the right place. Local dev points at the services
// via VITE_* / window.__ENV__ overrides.

const runtime = typeof window !== "undefined" ? window.__ENV__ ?? {} : {};

function resolve(runtimeValue: string | undefined, buildValue: string | undefined, fallback: string): string {
  return runtimeValue || buildValue || fallback;
}

// Derive a same-origin websocket origin from the current page (wss on https).
// SSR / non-browser contexts fall back to an empty string — the ws client only
// runs after mount where window is always present.
function sameOriginWsBase(): string {
  if (typeof window === "undefined") return "";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
}

export const config = {
  // authClient/Settings prepend "/auth/…", so an empty base means same-origin.
  authBaseUrl: resolve(runtime.AUTH_BASE_URL, import.meta.env.VITE_AUTH_BASE_URL, ""),
  // Used directly as the GraphQL URL — default to the same-origin /graphql path.
  graphqlEndpoint: resolve(runtime.GRAPHQL_ENDPOINT, import.meta.env.VITE_GRAPHQL_ENDPOINT, "/graphql"),
  // Used directly as the socket origin — derive wss/ws from window.location.
  wsEndpoint: resolve(runtime.WS_ENDPOINT, import.meta.env.VITE_WS_ENDPOINT, sameOriginWsBase()),
  // apiClient/auditClient prepend "/api/…" — empty base means same-origin.
  apiServiceUrl: resolve(runtime.API_SERVICE_URL, import.meta.env.VITE_API_SERVICE_URL, ""),
  // tenantClient prepends "/tenants/…" — empty base means same-origin.
  tenantBaseUrl: resolve(runtime.TENANT_BASE_URL, import.meta.env.VITE_TENANT_BASE_URL, ""),
  sentryDsn: runtime.SENTRY_DSN || import.meta.env.VITE_SENTRY_DSN || "",
  // Where the "Exit" control on the impersonation banner returns the operator.
  // Empty = no external portal configured → Exit just clears the session and
  // sends the tab to the local /login (the impersonation tab opened fresh).
  adminPortalUrl: runtime.ADMIN_PORTAL_URL || import.meta.env.VITE_ADMIN_PORTAL_URL || "",
} as const;

// The app talks ONLY to the live backend (auth-service, graphql-bff,
// api-service, tenant-service, websocket-service). There is no mock data layer.
